import {Dispatch, FC, SetStateAction} from "react";
import {ChevronLeft, ChevronRight} from "lucide-react";

interface Props {
  page: number;
  total: number;
  setPage: Dispatch<SetStateAction<number>>;
  loading: boolean;
}

// inactive bg: oklch(0.93 0.01 75) ≈ #EDE9E3  color: oklch(0.45 0.05 265) ≈ #8490AA
// active bg:   oklch(0.713 0.165 42.1) ≈ #F07340  color: white
const INACTIVE_STYLE = {backgroundColor: "#EDE9E3", color: "#8490AA"};
const ACTIVE_STYLE = {backgroundColor: "#F07340", color: "white"};

const Paginator: FC<Props> = ({setPage, page, total, loading}) => {
  const pagesLeft = total - page;

  const middlePages = Array.from({length: Math.min(2, total - 2)}, (_, i) => {
    if (total === 3) return 2;
    if (page === total - 1) return page - 1 + i;
    if (page + 3 > total) return page + i;
    if (page === 0) return page + i + 2;

    return page + i + 1;
  });

  const pageBtn = "w-7 h-7 rounded-lg text-xs font-semibold transition-all disabled:opacity-30";
  const chevBtn = "w-7 h-7 rounded-lg flex items-center justify-center transition-all disabled:opacity-30";

  return (
    <div className="flex items-center gap-1 select-none">
      {!total ? (
        <span className="text-xs" style={{color: "#8490AA"}}>
          ...
        </span>
      ) : (
        <>
          <button
            className={chevBtn}
            style={INACTIVE_STYLE}
            disabled={loading || page === 0}
            onClick={() => setPage((old) => Math.max(0, old - 1))}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <button
            className={pageBtn}
            style={page === 0 ? ACTIVE_STYLE : INACTIVE_STYLE}
            disabled={loading}
            onClick={() => setPage(0)}
          >
            1
          </button>

          {page > 1 && total > 4 && (
            <span className="text-xs px-0.5" style={{color: "#8490AA"}}>
              …
            </span>
          )}

          {middlePages.map((n) => (
            <button
              key={n}
              className={pageBtn}
              style={page === n - 1 ? ACTIVE_STYLE : INACTIVE_STYLE}
              disabled={loading}
              onClick={() => setPage(n - 1)}
            >
              {n}
            </button>
          ))}

          {total > 4 && pagesLeft > 3 && (
            <span className="text-xs px-0.5" style={{color: "#8490AA"}}>
              …
            </span>
          )}

          {total > 1 && (
            <button
              className={pageBtn}
              style={page === total - 1 ? ACTIVE_STYLE : INACTIVE_STYLE}
              disabled={loading}
              onClick={() => setPage(total - 1)}
            >
              {total}
            </button>
          )}

          <button
            className={chevBtn}
            style={INACTIVE_STYLE}
            disabled={loading || page === total - 1}
            onClick={() => setPage((old) => Math.min(total - 1, old + 1))}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
};

export default Paginator;
